import Button from "components/shared/button";
import Input from "components/shared/inputs/input";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import {
  useCreateAdminTutorialMutation,
  useCreateTutorialMutation,
  useLazyGetAdminTutorialCategoriesQuery,
  useLazyGetTutorialCategoriesQuery,
  useUpdateAdminTutorialMutation,
  useUpdateTutorialMutation,
  useUploadTutorialFileMutation,
} from "store/api";
import { getStoredUser } from "utils/helpers/localStorage";

type TutorialCategory = {
  code: string;
  value: string;
};

type TutorialRow = {
  id?: number | string;
  env?: number | string;
  environnement_id?: number | string;
  category_code?: string;
  description?: string;
  video?: any;
  videoName?: string;
};

type TutorialEditPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  mode?: "create" | "edit";
  row?: TutorialRow | null;
  envNumeric?: number;
  lang?: string;
  isAdminTutorial?: boolean;
  onSaved?: () => Promise<void> | void;
};

const TutorialEditPopup: React.FC<TutorialEditPopupProps> = ({
  isOpen,
  onClose,
  mode = "create",
  row,
  envNumeric,
  lang,
  isAdminTutorial = false,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [categories, setCategories] = useState<TutorialCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [video, setVideo] = useState<any>(null);
  const [videoPreview, setVideoPreview] = useState<string>("");
  const isEditMode = mode === "edit";
  const [getTutorialCategories] = useLazyGetTutorialCategoriesQuery();
  const [getAdminTutorialCategories] = useLazyGetAdminTutorialCategoriesQuery();
  const [uploadTutorialFile] = useUploadTutorialFileMutation();
  const [createTutorial] = useCreateTutorialMutation();
  const [createAdminTutorial] = useCreateAdminTutorialMutation();
  const [updateTutorial] = useUpdateTutorialMutation();
  const [updateAdminTutorial] = useUpdateAdminTutorialMutation();

  const { control, reset, getValues } = useCustomForm<{
    category_code: string;
    description: string;
  }>({
    defaultValues: {
      category_code: "",
      description: "",
    },
  });

  useEffect(() => {
      if (!isOpen) return;
      
      setCategoriesLoading(true);
      const loadCategories = async () => {
          try {
            const env = envNumeric ?? 1;
            const locale = lang || "fr";
            const data = await (isAdminTutorial
              ? getAdminTutorialCategories({ env, lang: locale }).unwrap()
              : getTutorialCategories({ env, lang: locale }).unwrap());
            
            let categoriesData: any[] = [];
            if (Array.isArray(data)) {
              categoriesData = data;
            } else if (Array.isArray(data?.data)) {
              categoriesData = data.data;
            }

            const cats = categoriesData
              .filter(Boolean)
              .map((i: any) => ({
                code: i.code,
                value: i.value,
              }));
            setCategories(cats);
        } catch (error) {
            console.error("Failed to load categories:", error);
        } finally {
            setCategoriesLoading(false);
        }
    };

    loadCategories();
  }, [isOpen, envNumeric, lang, isAdminTutorial, getAdminTutorialCategories, getTutorialCategories]);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && row) {
        reset({
          category_code: row.category_code || "",
          description: row.description || "",
        });
        const existingVideo = row.video ?? row.videoName ?? null;
        setVideo(existingVideo);
        setVideoPreview(row.videoName || (typeof row.video === "string" ? row.video : ""));
      } else {
        reset({ category_code: "", description: "" });
        setVideo(null);
        setVideoPreview("");
      }
    }
  }, [isOpen, row, mode, reset, isEditMode]);

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideo(file);
      setVideoPreview(file.name);
    }
  };

  const handleUploadClick = () => {
    if (isSaving) return;
    const input = document.getElementById("tutorial-video-input") as HTMLInputElement | null;
    input?.click();
  };

  const handleSave = async () => {
    const { category_code, description } = getValues();

    if (!category_code || !description || !video) {
      // Show validation error - implement as needed
      return;
    }

    setIsSaving(true);
    try {
      const selectedCategory = categories.find((cat) => cat.code === category_code);
      const storedUser = getStoredUser<{ id?: number | string }>();
      let videoPayload = video;
      const envToSend = envNumeric ?? Number(row?.environnement_id ?? row?.env ?? 1);

      if (video instanceof File) {
        videoPayload = await uploadTutorialFile({
          userId: storedUser?.id,
          name: video.name,
          file: video,
        }).unwrap();
      }

      if (isEditMode) {
        const rowId = row?.id;
        if (rowId == null) return;
        const payload = {
          id: rowId,
          env: envToSend,
          category_code,
          description,
          environnement_id: envToSend,
          lang,
          category: selectedCategory?.value,
          videoName: videoPreview || row?.videoName,
          video: videoPayload,
        };

        if (isAdminTutorial) {
          await updateAdminTutorial(payload).unwrap();
        } else {
          await updateTutorial(payload).unwrap();
        }
      } else {
        const payload = {
          env: envNumeric,
          environnement_id: envNumeric,
          category_code,
          description,
          lang,
          category: selectedCategory?.value,
          videoName: videoPreview,
          video: videoPayload,
        };

        if (isAdminTutorial) {
          await createAdminTutorial(payload).unwrap();
        } else {
          await createTutorial(payload).unwrap();
        }
      }

      await onSaved?.();
      onClose();
    } catch (error) {
      console.error("Failed to save tutorial:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const categoryOptions = categories.map((cat) => ({
    text: cat.value,
    value: cat.code,
  }));

  return (
    <Popup
      title={isEditMode ? t("Edit Tutorial") : t("Add Tutorial")}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button
              type="button"
              primary
              onClick={handleSave}
              disabled={isSaving}
            >
              {t("Update")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            {/* Category Select */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={t("Category")}
                  name="category_code"
                  control={control}
                  type="select"
                  size="large"
                  options={categoryOptions}
                  disabled={categoriesLoading}
                />
              </group>
            </group>

            {/* Description TextArea */}
            <group data-gap="20">
              <group data-length="forcefit" data-fit="1">
                <Input
                  dataLength="autofit"
                  label={t("Description")}
                  name="description"
                  control={control}
                  type="textarea"
                  size="large"
                  placeholder={t("Enter tutorial description")}
                />
              </group>
            </group>

            {/* Video Upload */}
            <group data-gap="20">
              <group data-direction="column" data-gap="8">
                <text data-size="14" data-weight="600">
                  {t("Video")}
                </text>
                <group data-gap="10" data-align="center">
                  <input
                    id="tutorial-video-input"
                    type="file"
                    // @ts-ignore - file input accept attribute
                    accept="video/*"
                    onChange={handleVideoChange}
                    disabled={isEditMode && mode === "edit"}
                    style={{ display: "none" }}
                  />
                  <Button
                    type="button"
                    primary
                    onClick={handleUploadClick}
                    disabled={isSaving || (isEditMode && mode === "edit")}
                  >
                    {t("Upload")}
                  </Button>
                  <text data-size="12" data-color="secondary">
                    {videoPreview || t("No file selected")}
                  </text>
                </group>
              </group>
            </group>
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default TutorialEditPopup;
